// Invio HTTP fire-and-forget. Non solleva mai (la telemetria non deve crashare
// l'host) e non ha retry loop.
//
// Auth (CYJS-1): due modi mutuamente esclusivi decisi a monte in base
// all'ambiente. `bearer` → header `Authorization: Bearer <secret>` (SERVER-ONLY:
// il bearer `cyi_` non deve mai finire nel bundle browser). `dsn` → DSN public
// key non segreta via query `?sentry_key=` (browser-safe): nessun header custom,
// così la preflight CORS resta su `Content-Type` soltanto.
//
// Browser: `fetch` con `keepalive: true` (solo sotto la quota body di 64KB di
// Chrome) così le richieste sopravvivono all'unload della pagina. I redirect
// restano al fetch nativo (`follow`): con `manual` la risposta sarebbe
// `opaqueredirect` senza Location leggibile.
//
// Browser, pagina in uscita (CYJS-29): `fetch(keepalive)` non basta quando la
// scheda si chiude — Safari iOS abbandona la richiesta e l'ultima misura si
// perde — quindi da `pagehide`/`visibilitychange(hidden)` in poi l'invio passa da
// `navigator.sendBeacon`. Usabile SOLO in modalità `dsn`: il beacon non può
// impostare header (in `bearer` partirebbe senza credenziale), mentre qui l'auth
// è la public key in query. Il body viaggia come `text/plain`: con
// `application/json` la richiesta non sarebbe CORS-simple e scatenerebbe una
// preflight a cui un invio in chiusura pagina non sopravvive (il backend parsa il
// body grezzo). Fallback a `fetch(keepalive)` dove il beacon manca o rifiuta il
// payload (quota per-origin superata).
//
// Le richieste GIÀ partite col fetch quando la pagina si chiude restano dove
// sono: NON vengono rispedite col beacon. Il reinvio le duplicherebbe ogni volta
// che il fetch arriva comunque (il caso normale), e una metrica contata due volte
// è un danno certo contro una perdita solo possibile. È `keepalive` a coprirle,
// per quel che può.
//
// Node: `redirect:'manual'` + follow a mano (max 2) ri-POSTando lo stesso body —
// un 301 apex→www con `follow` degraderebbe il POST in GET (parità con il
// Transport ruby/dart, MAX_REDIRECTS=2). In più viaggia lo User-Agent
// `closeyourit-js/{versione}` (nel browser è vietato dal fetch spec).

import { errorName } from './diagnostics'
import type { TransportAuth } from './options'
import { isBrowser, isNode } from './runtime'
import type { Stats } from './stats'
import type { DiagnosticDetails, DiagnosticEvent } from './types'
import { SDK_NAME, SDK_VERSION } from './version'

type NotifyDiagnostic = (event: DiagnosticEvent, details?: DiagnosticDetails) => void

type FetchImpl = typeof fetch

/// Invio "beacon" (firma di `navigator.sendBeacon`): `true` se il browser ha
/// preso in carico la richiesta, `false` se l'ha rifiutata (quota superata).
export type BeaconImpl = (url: string, body: BodyInit) => boolean

const MAX_REDIRECTS = 2
// Quota keepalive di Chrome: 64KB per-origin sui body in volo. Oltre la soglia
// (con margine) l'invio parte senza keepalive: perde solo la garanzia di
// sopravvivere all'unload, ma non fallisce con TypeError.
const KEEPALIVE_MAX_BODY = 60_000
const REDIRECT_STATUSES = new Set([301, 302, 303, 307, 308])
// Il solo Content-Type che tiene il beacon dentro le richieste CORS-simple: con
// `application/json` scatterebbe la preflight e l'invio in chiusura pagina non le
// sopravvivrebbe. È anche il tipo che la spec assegna a un beacon di sola stringa.
const BEACON_CONTENT_TYPE = 'text/plain;charset=UTF-8'

export interface TransportOptions {
  auth: TransportAuth
  stats: Stats
  fetchImpl?: FetchImpl | undefined
  /// Invio beacon iniettabile (test). Default: `navigator.sendBeacon`, risolto a
  /// ogni invio — l'SDK può essere inizializzato prima che il DOM esista (SSR).
  beaconImpl?: BeaconImpl | undefined
  maxQueue?: number
  timeoutMs?: number
  debug?: (message: string, error?: unknown) => void
  /// Notificatore diagnostico (process-local, non ricorsivo): riceve le tappe di
  /// invio/scarto/timeout per l'hook `onDiagnostic` (CYJS-9).
  diagnostic?: NotifyDiagnostic
}

export class Transport {
  private readonly auth: TransportAuth
  private readonly stats: Stats
  private readonly fetchImpl: FetchImpl | undefined
  private readonly beaconImpl: BeaconImpl | undefined
  private readonly maxQueue: number
  private readonly timeoutMs: number
  private readonly debug: ((message: string, error?: unknown) => void) | undefined
  private readonly diagnostic: NotifyDiagnostic

  private inFlight = 0
  private readonly pending = new Set<Promise<void>>()
  // La pagina sta uscendo di scena (`pagehide`/`visibilitychange(hidden)`): lo
  // segnala il lifecycle del facade, non lo si indovina qui.
  private pageHiding = false

  constructor(options: TransportOptions) {
    this.auth = options.auth
    this.stats = options.stats
    this.fetchImpl =
      options.fetchImpl ??
      (typeof fetch === 'function' ? fetch.bind(globalThis) : undefined)
    this.beaconImpl = options.beaconImpl
    this.maxQueue = options.maxQueue ?? 30
    this.timeoutMs = options.timeoutMs ?? 5000
    this.debug = options.debug
    this.diagnostic = options.diagnostic ?? (() => {})
  }

  /// Segnala che la pagina sta uscendo di scena (`pagehide`,
  /// `visibilitychange(hidden)`) o vi è rientrata (`pageshow` da bfcache). Da
  /// pagina in uscita gli invii browser in modalità `dsn` passano dal beacon:
  /// è l'unico che il browser porta a termine dopo la chiusura della scheda
  /// (CYJS-29). Nessun effetto su Node né in modalità `bearer`.
  setPageHiding(hiding: boolean): void {
    this.pageHiding = hiding
  }

  /// Invia [payload] (oggetto o array) a [url]. Ritorna `true` se l'invio è stato
  /// accettato (beacon preso in carico o richiesta in coda), `false` se scartato
  /// (coda piena o nessun trasporto disponibile).
  send(payload: unknown, url: string): boolean {
    const body = JSON.stringify(payload)

    // Pagina in uscita: il beacon è sincrono, non consuma la coda in volo e
    // sopravvive alla chiusura della scheda. Se manca o rifiuta il payload si
    // prosegue col fetch qui sotto — un invio perso è peggio di uno senza
    // garanzia di sopravvivenza.
    if (this.beaconEligible() && this.sendViaBeacon(body, url)) return true

    const fetchImpl = this.fetchImpl
    if (!fetchImpl) {
      // Nessun `fetch` nell'ambiente: l'evento non ha modo di partire → fallimento
      // di trasporto (parità col contatore `failed`, motivo `network`).
      this.stats.record('network')
      this.diagnostic('drop', { reason: 'network' })
      return false
    }
    if (this.inFlight >= this.maxQueue) {
      this.stats.record('queue_full')
      this.diagnostic('drop', { reason: 'queue_full' })
      this.debug?.('coda piena, evento scartato')
      return false
    }

    const controller = createAbortController()
    const timer =
      controller && this.timeoutMs > 0
        ? setTimeout(() => controller.abort(), this.timeoutMs)
        : undefined
    const init: RequestInit = {
      method: 'POST',
      headers: this.headers(),
      body,
      ...(isBrowser() && body.length < KEEPALIVE_MAX_BODY ? { keepalive: true } : {}),
      ...(isNode() ? { redirect: 'manual' as RequestRedirect } : {}),
      ...(controller ? { signal: controller.signal } : {}),
    }

    this.inFlight += 1
    const promise = this.perform(fetchImpl, this.withAuth(url), init)
      .catch((error: unknown) => {
        // Un abort è il nostro timeout di rete (isolato dai non-2xx, parità gem);
        // ogni altra reject di `fetch` è un errore di rete generico.
        if (isAbortError(error)) {
          this.stats.recordTimeout()
          this.diagnostic('timeout', { error: errorName(error) })
        } else {
          this.stats.record('network')
          this.diagnostic('drop', { reason: 'network', error: errorName(error) })
        }
        this.debug?.('invio fallito', error)
      })
      .finally(() => {
        this.inFlight -= 1
        if (timer !== undefined) clearTimeout(timer)
        this.pending.delete(promise)
      })

    this.pending.add(promise)
    return true
  }

  /// Attende gli invii in volo (best-effort; non solleva). Il beacon è già
  /// consegnato al browser al ritorno di `send`: qui si attende solo la sua
  /// contabilizzazione, rinviata a fine turno.
  async flush(): Promise<void> {
    await Promise.allSettled([...this.pending])
  }

  // Il beacon vale solo nel browser e solo con l'auth in query (`?sentry_key=`):
  // non potendo impostare header, in modalità `bearer` l'invio partirebbe senza
  // credenziale. `visibilityState` copre il caso in cui la pagina è già nascosta
  // prima che il lifecycle dell'SDK sia in ascolto (init a scheda in background).
  private beaconEligible(): boolean {
    if (this.auth.kind !== 'dsn' || !isBrowser()) return false
    return this.pageHiding || documentHidden()
  }

  // Consegna il body al browser via beacon. `false` = non consegnato (beacon
  // assente, quota superata o eccezione): il chiamante ripiega sul fetch.
  private sendViaBeacon(body: string, url: string): boolean {
    const beacon = this.beaconImpl ?? navigatorBeacon()
    if (!beacon) return false
    try {
      if (!beacon(this.withAuth(url), beaconBody(body))) return false
    } catch (error) {
      this.debug?.('beacon non riuscito', error)
      return false
    }
    // L'invio è già nelle mani del browser, ma l'esito si contabilizza in coda al
    // turno: `send` è sincrono solo qui, e chi lo chiama deve poter registrare
    // l'accodamento per primo — l'ordine diagnostico resta `enqueue` → `send`
    // come nel ramo fetch, senza snapshot intermedi con più inviati che accodati.
    // La notifica entra in `pending`, così `flush()` la attende.
    const notified = Promise.resolve()
      .then(() => {
        // Il beacon non restituisce una risposta: `sent` qui significa "preso in
        // carico dal browser" e la diagnostica non ha uno status da riportare.
        this.stats.increment('sent')
        this.diagnostic('send', {})
      })
      .finally(() => {
        this.pending.delete(notified)
      })

    this.pending.add(notified)
    return true
  }

  // Esegue la richiesta seguendo a mano i redirect su Node (POST preservato).
  private async perform(
    fetchImpl: FetchImpl,
    url: string,
    init: RequestInit,
  ): Promise<void> {
    let currentUrl = url
    let response = await fetchImpl(currentUrl, init)

    if (isNode()) {
      let redirects = 0
      while (REDIRECT_STATUSES.has(response.status) && redirects < MAX_REDIRECTS) {
        const location = response.headers?.get('location')
        if (!location) break
        const nextUrl = new URL(location, currentUrl).toString()
        // Il follow ri-invia Authorization: consentito SOLO verso lo stesso
        // host o la variante www (il caso reale apex→www). Un Location verso
        // un host estraneo esfiltrerebbe il Bearer → stop.
        if (!allowedRedirectTarget(currentUrl, nextUrl)) break
        currentUrl = this.withAuth(nextUrl)
        redirects += 1
        response = await fetchImpl(currentUrl, init)
      }
    }

    if (response.status >= 200 && response.status < 300) {
      this.stats.increment('sent')
      this.diagnostic('send', { status: response.status })
    } else {
      // Non-2xx (4xx / 429 / 5xx): l'evento è partito ma è stato rifiutato →
      // fallimento d'invio. Lo `status` distingue i casi nell'hook diagnostico.
      this.stats.record('response')
      this.diagnostic('drop', { reason: 'response', status: response.status })
      if (this.debug) {
        // Include il codice R- + messaggio dall'envelope backend (DX diagnostica), non solo lo status.
        const detail = await errorDetail(response)
        this.debug(`invio fallito con stato ${response.status}${detail}`)
      }
    }
  }

  private headers(): Record<string, string> {
    return {
      // Il bearer viaggia in header solo in modalità server. In `dsn` la
      // credenziale è la public key in query (?sentry_key=), mai un header.
      ...(this.auth.kind === 'bearer'
        ? { Authorization: `Bearer ${this.auth.secret}` }
        : {}),
      'Content-Type': 'application/json',
      // Nel browser lo User-Agent è un forbidden header (lo imposta il browser).
      ...(isNode() ? { 'User-Agent': `${SDK_NAME}/${SDK_VERSION}` } : {}),
    }
  }

  // In modalità `dsn` autentica appendendo la DSN public key come query param
  // (browser-safe, non allarga la preflight CORS). Idempotente: non raddoppia il
  // param se già presente (es. ri-invio su redirect follow).
  private withAuth(url: string): string {
    if (this.auth.kind !== 'dsn') return url
    if (url.includes('sentry_key=')) return url
    const separator = url.includes('?') ? '&' : '?'
    return `${url}${separator}sentry_key=${encodeURIComponent(this.auth.publicKey)}`
  }
}

function createAbortController(): AbortController | undefined {
  return typeof AbortController === 'function' ? new AbortController() : undefined
}

// `navigator.sendBeacon` legato al suo receiver (chiamarlo staccato solleva
// "Illegal invocation"). `undefined` fuori dal browser o dove non esiste.
function navigatorBeacon(): BeaconImpl | undefined {
  if (typeof navigator === 'undefined') return undefined
  const beacon = navigator.sendBeacon
  return typeof beacon === 'function'
    ? (url, body) => beacon.call(navigator, url, body)
    : undefined
}

// Body del beacon come `text/plain` (richiesta CORS-simple, nessuna preflight).
// Senza `Blob` nell'ambiente si manda la stringa: per spec il browser le assegna
// comunque `text/plain;charset=UTF-8`.
function beaconBody(body: string): BodyInit {
  if (typeof Blob !== 'function') return body
  return new Blob([body], { type: BEACON_CONTENT_TYPE })
}

// `true` quando la pagina non è visibile (scheda in background o in chiusura).
function documentHidden(): boolean {
  return typeof document !== 'undefined' && document.visibilityState === 'hidden'
}

// `controller.abort()` (il nostro timeout) fa rigettare `fetch` con un AbortError.
function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError'
}

// Stesso host oppure www-swap (`closeyour.it` ⇄ `www.closeyour.it`).
function allowedRedirectTarget(fromUrl: string, toUrl: string): boolean {
  try {
    const from = new URL(fromUrl).host
    const to = new URL(toUrl).host
    return from === to || `www.${from}` === to || from === `www.${to}`
  } catch {
    return false
  }
}

// Estrae " (R…: messaggio)" dall'envelope d'errore del backend per il log diagnostico.
async function errorDetail(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as {
      error?: { code?: string; message?: string }
    }
    const error = body?.error
    if (!error) return ''
    const parts = [error.code, error.message].filter(Boolean)
    return parts.length > 0 ? ` (${parts.join(': ')})` : ''
  } catch {
    return ''
  }
}
